import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Switch, FormControlLabel,
  Button, Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  Chip, Tabs, Tab, Stack, Alert, Paper, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Notifications, NotificationsActive, Settings, Refresh, DoneAll,
  Check, Delete, DesktopWindows, Email, Telegram, PhoneAndroid,
  TrendingUp, Security, CheckCircle, Warning, Speed, PriorityHigh, Launch,
} from '@mui/icons-material';
import { notifications as notifApi } from '../services/api';
import {
  checkNotificationPermission, requestNotificationPermission,
  showDesktopNotification, testBrowserDesktopNotification,
} from '../services/desktopNotificationService';
import NotificationDetailModal from '../components/NotificationCenter/NotificationDetailModal';
import toast from 'react-hot-toast';

const CATEGORY_NAMES = {
  EQUITY_BUY: 'Equity Buy Recommendations',
  EQUITY_SELL: 'Equity Sell Signals',
  OPTION_BUY: 'Option Buy Opportunities',
  OPTION_SELL: 'Option Exit Signals',
  AUTO_BUY: 'Auto Buy Execution',
  AUTO_SELL: 'Auto Sell Execution',
  TRAILING_SL: 'Trailing Stop Loss Updates',
  TARGET: 'Target Approaching / Target Hit',
  STOP_LOSS: 'Stop Loss Triggered',
  AI_DECISION_CHANGE: 'AI Decision Updates (Hold/Trail)',
  MARKET_STATUS: 'Market Open / Closing / Closed',
  ZERODHA_CONNECTION: 'Zerodha Connection Status',
};

export default function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const notifIdFromUrl = searchParams.get('id');

  const [activeTab, setActiveTab] = useState(0); // 0 = History, 1 = Preferences, 2 = Diagnostics
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [permission, setPermission] = useState(checkNotificationPermission());

  // Notification Detail Modal state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Preferences State
  const [settings, setSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  // Telegram Group State
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [loadingDetect, setLoadingDetect] = useState(false);
  const [loadingTestTg, setLoadingTestTg] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await notifApi.list({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotal(res.data.meta?.total || 0);
        setUnreadCount(res.data.meta?.unreadCount || 0);
      }
    } catch (err) {
      toast.error('Failed to load notification history');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await notifApi.getSettings();
      if (res.data?.success) {
        setSettings(res.data.data || {});
      }
    } catch (err) {
      toast.error('Failed to load notification preferences');
    }
  };

  const fetchTelegramStatus = async () => {
    try {
      const res = await notifApi.getTelegramStatus();
      if (res.data?.success) {
        setTelegramStatus(res.data.data);
      }
    } catch (err) {
      console.warn('Failed to fetch Telegram status:', err.message);
    }
  };

  const handleDetectTelegram = async () => {
    setLoadingDetect(true);
    try {
      const res = await notifApi.detectTelegramChatId();
      if (res.data?.success && res.data?.found) {
        toast.success(res.data.message || 'Telegram Group detected and connected!');
        await fetchTelegramStatus();
      } else {
        toast.error(res.data?.message || 'No Telegram messages found yet. Please send a message in your group.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to detect Telegram group');
    } finally {
      setLoadingDetect(false);
    }
  };

  const handleTestTelegramNotification = async () => {
    setLoadingTestTg(true);
    try {
      const res = await notifApi.testTelegram();
      if (res.data?.success) {
        toast.success('Test alert sent to Telegram group successfully!');
      } else {
        toast.error(res.data?.message || 'Failed to send to Telegram');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send Telegram test message');
    } finally {
      setLoadingTestTg(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchSettings();
    fetchTelegramStatus();
  }, [page, rowsPerPage]);

  // Handle opening notification detail if id URL param is present
  useEffect(() => {
    if (notifIdFromUrl) {
      // First search local items list
      const found = items.find(item => String(item.id) === String(notifIdFromUrl));
      if (found) {
        setSelectedNotification(found);
        setModalOpen(true);
        if (!found.is_read) handleMarkAsRead(found.id);
      } else {
        // Fetch from API directly
        notifApi.getById(notifIdFromUrl)
          .then((res) => {
            if (res.data?.success && res.data.data) {
              setSelectedNotification(res.data.data);
              setModalOpen(true);
              if (!res.data.data.is_read) handleMarkAsRead(res.data.data.id);
            }
          })
          .catch(() => {
            console.warn('Could not load specific notification detail for ID:', notifIdFromUrl);
          });
      }
    }
  }, [notifIdFromUrl, items]);

  const handleEnableDesktop = async () => {
    const res = await requestNotificationPermission();
    setPermission(res);
    if (res === 'granted') {
      toast.success('Desktop Notifications Granted!');
      showDesktopNotification({
        title: '🟢 BROWSER DESKTOP NOTIFICATIONS ACTIVE',
        message: 'Native desktop popups are enabled. You will receive alerts across any browser tab/window!',
        priority: 'HIGH',
      });
    } else {
      toast.error('Browser desktop notification permission was denied.');
    }
  };

  const handleTogglePref = (category, channel) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [channel]: !prev[category]?.[channel],
      },
    }));
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await notifApi.updateSettings(settings);
      toast.success('Notification settings saved successfully');
    } catch (err) {
      toast.error('Failed to save notification settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTestBackendNotification = async (type) => {
    try {
      const res = await notifApi.test({ type });
      toast.success('Test notification generated from backend!');
      fetchHistory();
      if (res.data?.data) {
        showDesktopNotification(res.data.data);
      }
    } catch (err) {
      toast.error('Failed to trigger test notification');
    }
  };

  const handleTestBrowserNativeNotification = () => {
    testBrowserDesktopNotification({
      title: '🟢 NATIVE BROWSER DESKTOP NOTIFICATION',
      message: 'This is a native OS notification. Click me now to test redirecting directly to this notification detail!',
    });
    toast.success('Native Desktop Popup sent! Check your system notification drawer.');
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notifApi.markAsRead(id);
      setItems(prev => prev.map(item => item.id === id ? { ...item, is_read: 1 } : item));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notifApi.markAllAsRead();
      setItems(prev => prev.map(item => ({ ...item, is_read: 1 })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch (err) {
      toast.error('Failed to mark all read');
    }
  };

  const handleRowClick = (row) => {
    setSelectedNotification(row);
    setModalOpen(true);
    setSearchParams({ id: row.id });
    if (!row.is_read) {
      handleMarkAsRead(row.id);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedNotification(null);
    setSearchParams({});
  };

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Browser Desktop & In-App Notification Center
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Receive instant native OS desktop popups on all browsers for Equity, Options, Auto Trading, and Trailing SL
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {permission !== 'granted' ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DesktopWindows />}
              onClick={handleEnableDesktop}
              sx={{ fontWeight: 700 }}
            >
              Enable Browser Desktop Notifications
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<NotificationsActive />}
              onClick={handleTestBrowserNativeNotification}
              sx={{ fontWeight: 700 }}
            >
              Test Native Desktop Popup
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => { fetchHistory(); fetchSettings(); }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Permission Alert Banner */}
      {permission !== 'granted' ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleEnableDesktop} sx={{ fontWeight: 700 }}>
              Allow Access Now
            </Button>
          }
          sx={{ mb: 3, borderRadius: 2 }}
        >
          Browser Desktop Notification status is <b>{permission.toUpperCase()}</b>. Click allow to receive real-time OS popups across any browser window or tab even when minimized!
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          🟢 <b>Native Browser Desktop Notifications Active</b>. Clicking any desktop popup will automatically focus your browser and open its full detail view.
        </Alert>
      )}

      {/* Main Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#fff' }}
        >
          <Tab label={`Notification Log (${total})`} />
          <Tab label="Notification Preferences & Channels" />
          <Tab label="Test & Diagnostics" />
        </Tabs>

        {/* TAB 0: HISTORY LOG */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recent Alerts ({unreadCount} unread)
              </Typography>
              {unreadCount > 0 && (
                <Button size="small" startIcon={<DoneAll />} onClick={handleMarkAllRead}>
                  Mark All Read
                </Button>
              )}
            </Box>

            {loading ? (
              <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
            ) : items.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <Notifications sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body1">No notifications recorded yet.</Typography>
              </Box>
            ) : (
              <Table container component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell><b>Time</b></TableCell>
                    <TableCell><b>Category / Priority</b></TableCell>
                    <TableCell><b>Symbol</b></TableCell>
                    <TableCell><b>Title & Message</b></TableCell>
                    <TableCell><b>Mode</b></TableCell>
                    <TableCell><b>Action</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        bgcolor: String(row.id) === String(notifIdFromUrl)
                          ? 'rgba(14, 165, 233, 0.15)'
                          : (row.is_read ? '#fff' : '#f0f9ff'),
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#e0f2fe' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                        {new Date(row.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={row.priority}
                            color={row.priority === 'CRITICAL' ? 'error' : row.priority === 'HIGH' ? 'warning' : 'info'}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                          />
                          <Chip label={row.category} variant="outlined" size="small" sx={{ fontSize: '0.65rem' }} />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{row.symbol || 'SYSTEM'}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {row.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
                          {row.message.length > 120 ? `${row.message.slice(0, 120)}...` : row.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.mode || 'SYSTEM'} size="small" color={row.mode === 'LIVE' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View full detail">
                          <IconButton size="small" onClick={() => handleRowClick(row)}>
                            <Launch fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
          </Box>
        )}

        {/* TAB 1: PREFERENCES */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Category Channel Rules & Controls
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSettings}
                disabled={savingSettings}
              >
                {savingSettings ? 'Saving...' : 'Save Preferences'}
              </Button>
            </Box>

            <Grid container spacing={2}>
              {Object.entries(CATEGORY_NAMES).map(([key, label]) => {
                const pref = settings[key] || { desktop: true, inApp: true, email: true, telegram: true };
                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                          {label}
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={pref.desktop}
                                  onChange={() => handleTogglePref(key, 'desktop')}
                                />
                              }
                              label={<Typography variant="caption">Desktop Popups</Typography>}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={pref.inApp}
                                  onChange={() => handleTogglePref(key, 'inApp')}
                                />
                              }
                              label={<Typography variant="caption">In-App Center</Typography>}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={pref.email}
                                  onChange={() => handleTogglePref(key, 'email')}
                                />
                              }
                              label={<Typography variant="caption">Email Alerts</Typography>}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={pref.telegram}
                                  onChange={() => handleTogglePref(key, 'telegram')}
                                />
                              }
                              label={<Typography variant="caption">Telegram Bot</Typography>}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* TAB 2: TEST & DIAGNOSTICS */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Left Column: Telegram Group Alerts */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', borderColor: '#38bdf8' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Telegram sx={{ color: '#0284c7', fontSize: 32 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          Telegram Group Alerts
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Real-time trading notifications delivered directly to your Telegram group
                        </Typography>
                      </Box>
                    </Box>

                    {telegramStatus?.ready ? (
                      <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Group Connected & Ready!
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          Bot: <b>@{telegramStatus?.bot?.username}</b><br />
                          Chat ID: <code>{telegramStatus?.chatId}</code>
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Group Connection Setup Required
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', mt: 0.5 }}>
                          1. Create your Telegram group.<br />
                          2. Add <b>@{telegramStatus?.bot?.username || 'JigendraTradingAlerts_bot'}</b> as a member/admin.<br />
                          3. Send any message in the group (e.g., <i>"hello"</i>).<br />
                          4. Click <b>Auto-Detect Telegram Group</b> below!
                        </Typography>
                      </Alert>
                    )}

                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: '#0284c7',
                          '&:hover': { backgroundColor: '#0369a1' },
                          py: 1.3,
                          fontWeight: 700,
                        }}
                        startIcon={loadingDetect ? <CircularProgress size={20} color="inherit" /> : <Telegram />}
                        onClick={handleDetectTelegram}
                        disabled={loadingDetect}
                      >
                        {loadingDetect ? 'Scanning Telegram Updates...' : '1. Auto-Detect Telegram Group'}
                      </Button>

                      <Button
                        variant="outlined"
                        sx={{
                          borderColor: '#0284c7',
                          color: '#0284c7',
                          py: 1.3,
                          fontWeight: 700,
                        }}
                        startIcon={loadingTestTg ? <CircularProgress size={20} color="inherit" /> : <NotificationsActive />}
                        onClick={handleTestTelegramNotification}
                        disabled={loadingTestTg || !telegramStatus?.hasChatId}
                      >
                        {loadingTestTg ? 'Sending Alert...' : '2. Send Test Alert to Telegram Group'}
                      </Button>

                      <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px dashed #cbd5e1' }}>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>
                          <b>Bot Token:</b> Configured (Ends with <code>...3H4</code>)
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>
                          <b>Current Chat ID:</b> {telegramStatus?.chatId ? <code>{telegramStatus?.chatId}</code> : 'None (click detect above)'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>
                          <b>Status:</b> {telegramStatus?.enabled ? '✅ Notifications Enabled' : '❌ Disabled in Settings'}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column: Desktop & Browser Diagnostics */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Browser & Engine Diagnostics
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5 }}>
                      Trigger test notifications to verify native OS desktop popups and real-time Socket.io events.
                    </Typography>

                    <Stack direction="column" spacing={1.8}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<NotificationsActive />}
                        onClick={handleTestBrowserNativeNotification}
                        sx={{ py: 1.2, fontWeight: 700 }}
                      >
                        Test Browser Native Desktop Popup
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={() => handleTestBackendNotification('EQUITY_BUY_RECOMMENDATION')}
                        sx={{ py: 1.1 }}
                      >
                        Trigger Backend Buy Recommendation Alert
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleTestBackendNotification('TRAILING_SL_UPDATED')}
                        sx={{ py: 1.1 }}
                      >
                        Trigger Backend Trailing SL Update Alert
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleTestBackendNotification('MARKET_OPEN')}
                        sx={{ py: 1.1 }}
                      >
                        Trigger Market Status Alert
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Notification Detail Modal */}
      <NotificationDetailModal
        open={modalOpen}
        notification={selectedNotification}
        onClose={handleCloseModal}
        onMarkAsRead={handleMarkAsRead}
      />
    </Box>
  );
}
